import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import '@theme/dialog';
import '@theme/theme-drawer';

// A small adapter around the existing public search/cart methods. Native commerce,
// cart events and predictive search remain their only engines.
class FDHeader extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.records = new Map();
    this.queue = Promise.resolve();
    this.sequence = 0;
    const signal = this.abort.signal;
    const options = { signal };
    this.addEventListener('click', event => {
      const trigger = event.target.closest('[data-fd-overlay]');
      if (trigger) {
        event.preventDefault();
        this.request(trigger.dataset.fdOverlay, trigger);
        return;
      }
      const close = event.target.closest('[data-fd-close]');
      if (close) this.close(close.closest('dialog').dataset.fdPanel);
      const disclosure = event.target.closest('[data-fd-disclosure]');
      if (disclosure) {
        const expanded = disclosure.getAttribute('aria-expanded') !== 'true';
        disclosure.setAttribute('aria-expanded', String(expanded));
        this.querySelector(`#${disclosure.getAttribute('aria-controls')}`).hidden = !expanded;
        disclosure.querySelector('span').textContent = expanded ? '−' : '+';
      }
    }, options);
    for (const panel of this.querySelectorAll('[data-fd-panel]')) {
      const name = panel.dataset.fdPanel;
      this.records.set(name, { panel, open: () => panel.showModal(), close: () => panel.close() });
      panel.addEventListener('cancel', event => { event.preventDefault(); this.close(name); }, options);
      panel.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...panel.querySelectorAll('a[href],button:not(:disabled),select,input:not([type=hidden])')].filter(control => control.getClientRects().length);
        const first = controls[0], last = controls.at(-1);
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }, options);
      panel.addEventListener('click', event => {
        if (event.target !== panel) return;
        const rect = panel.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close(name);
      }, options);
    }
    this.ready = Promise.all([customElements.whenDefined('dialog-component'), customElements.whenDefined('theme-drawer')]).then(() => {
      if (!this.isConnected || signal.aborted) return;
      this.adapt('search', document.getElementById('search-modal'), 'showDialog', 'closeDialog', 'dialog');
      this.adapt('cart', document.getElementById('cart-drawer'), 'open', 'close', 'dialog');
    });
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.querySelector('.fd-shell__bar'));
    this.resizeObserver.observe(this.querySelector('.fd-shell__inline'));
    this.resizeObserver.observe(this.querySelector('.fd-shell__cart'));
    window.addEventListener('resize', () => this.measure(), options);
    document.fonts.ready.then(() => { if (this.isConnected) this.measure(); });
    document.addEventListener('shopify:section:select', event => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.request('navigation', this.querySelector('.fd-shell__menu'));
    }, options);
    document.addEventListener('shopify:section:deselect', event => {
      if (event.detail?.sectionId === this.dataset.sectionId) for (const name of this.records.keys()) this.close(name);
    }, options);
    this.measure();
    // Shopify is authoritative. Persistence records completion, never an enforced locale.
    this.languagePreferenceKey = 'fd-language-entry-seen-v3';
    this.languagePendingKey = 'fd-language-entry-pending-v3';
    // Retire persistent completion; Shopify alone owns the selected locale.
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name].removeItem('fd-language-confirmed-v2'); window[name].removeItem('fd-language-pending-v2'); } catch { /* Restricted storage. */ }
    }
    const previewUrl = new URL(location.href);
    if (window.Shopify?.theme?.role === 'unpublished' && previewUrl.searchParams.get('fd_reset_language') === '1') {
      for (const name of ['localStorage', 'sessionStorage']) {
        try { window[name].removeItem(this.languagePreferenceKey); window[name].removeItem(this.languagePendingKey); } catch { /* Restricted storage. */ }
      }
      previewUrl.searchParams.delete('fd_reset_language'); history.replaceState({}, '', previewUrl);
      // Shopify rendered return_to before the QA flag was removed from history.
      // Remove only that flag, never transform a locale prefix or product handle.
      for (const input of this.querySelectorAll('form input[name="return_to"]')) {
        const destination = new URL(input.value, location.origin);
        destination.searchParams.delete('fd_reset_language');
        input.value = destination.pathname + destination.search;
      }
    }
    this.confirmLanguageArrival();
    this.addEventListener('submit', event => {
      const panel = event.target.closest('[data-fd-panel]');
      if (!panel || !['welcome', 'localization'].includes(panel.dataset.fdPanel) || event.defaultPrevented) return;
      const language = event.submitter?.name === 'language_code'
        ? event.submitter.value : event.target.elements.language_code?.value;
      if (!language) return;
      if (language === document.documentElement.lang) {
        this.rememberLanguage();
        if (panel.dataset.fdPanel === 'welcome') { event.preventDefault(); this.close('welcome'); }
      } else {
        // Both forms submit natively to Shopify. Only the destination page confirms success.
        try { sessionStorage.setItem(this.languagePendingKey, language); } catch { /* Native submission still works. */ }
      }
    }, options);
    this.ready.then(() => {
      if (!this.isConnected || signal.aborted || window.Shopify?.designMode || !this.records.has('welcome')) return;
      let accepted = false;
      try { accepted = sessionStorage.getItem(this.languagePreferenceKey) === '1'; } catch { /* Native localization still works without storage. */ }
      if (!accepted && ![...this.records.values()].some(record => record.panel.open)) this.request('welcome', null);
    });
  }

  confirmLanguageArrival() {
    try {
      const pending = sessionStorage.getItem(this.languagePendingKey);
      if (pending === document.documentElement.lang) this.rememberLanguage();
      sessionStorage.removeItem(this.languagePendingKey);
    } catch { /* Native locale remains authoritative without storage. */ }
  }

  rememberLanguage() {
    try { sessionStorage.setItem(this.languagePreferenceKey, '1'); } catch { /* No persistence available. */ }
  }

  adapt(name, host, openMethod, closeMethod, selector) {
    if (!host) return;
    const originalOpen = host[openMethod];
    const originalClose = host[closeMethod];
    const record = { panel: host.querySelector(selector), host, open: originalOpen.bind(host), close: originalClose.bind(host) };
    this.records.set(name, record);
    const wrappedOpen = (...args) => this.request(name, document.activeElement, args);
    const wrappedClose = () => this.close(name);
    host[openMethod] = wrappedOpen;
    host[closeMethod] = wrappedClose;
    record.restore = () => {
      if (host[openMethod] === wrappedOpen) host[openMethod] = originalOpen;
      if (host[closeMethod] === wrappedClose) host[closeMethod] = originalClose;
    };
    if (record.panel?.open) { record.scroll = getScrollTop(); lockScroll(record.panel); }
  }

  request(name, trigger, args = []) {
    const sequence = ++this.sequence;
    const signal = this.abort.signal;
    const run = async () => {
      await this.ready;
      if (!this.isConnected || signal.aborted || sequence !== this.sequence) return;
      const record = this.records.get(name);
      if (!record) return;
      // Finish native closing animations before opening the next modal or trap.
      for (const [otherName, other] of this.records) {
        if (otherName !== name && (other.panel.open || other.closing)) await this.close(otherName, false);
      }
      if (record.closing) await record.closing;
      if (!this.isConnected || signal.aborted || sequence !== this.sequence) return;
      if (record.panel.open) return;
      record.trigger = trigger instanceof HTMLElement ? trigger : null;
      record.scroll = getScrollTop();
      // A trigger inside a now-hidden panel cannot receive restored focus.
      record.returnTo = record.trigger?.closest('dialog') ? this.querySelector('.fd-shell__menu') : record.trigger;
      lockScroll(record.panel);
      record.open(...args);
      // Native search opens on the next frame; cart opens synchronously.
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (!this.isConnected || signal.aborted) return;
      this.syncAria();
      if (name === 'navigation' || name === 'localization' || name === 'welcome') record.panel.querySelector('[data-fd-close]')?.focus({ preventScroll: true });
    };
    this.queue = this.queue.then(run).catch(error => { console.error('[fd-shell] Overlay could not open', error); });
    return this.queue;
  }

  async close(name, restoreFocus = true) {
    const record = this.records.get(name);
    if (!record) return;
    if (record.closing) return record.closing;
    if (!record.panel.open) return;
    if (name === 'welcome') this.rememberLanguage();
    record.closing = (async () => {
      await record.close();
      unlockScroll(record.panel);
      this.syncAria();
      if (restoreFocus && this.isConnected) {
        const fallback = this.querySelector(name === 'cart' ? '.fd-shell__cart [data-fd-overlay]' : name === 'search' ? '.fd-shell__search' : '.fd-shell__menu');
        const target = record.returnTo?.isConnected ? record.returnTo : fallback;
        target?.focus({ preventScroll: true });
      }
      if (record.scroll != null) scrollTo({ top: record.scroll, behavior: 'instant' });
    })();
    try { await record.closing; } finally { record.closing = null; }
  }

  syncAria() {
    for (const trigger of this.querySelectorAll('[data-fd-overlay]')) {
      trigger.setAttribute('aria-expanded', String(Boolean(this.records.get(trigger.dataset.fdOverlay)?.panel.open)));
    }
  }

  measure() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const bar = this.querySelector('.fd-shell__bar');
      const nav = this.querySelector('.fd-shell__inline');
      const style = getComputedStyle(bar);
      const available = bar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const required = nav.scrollWidth + this.querySelector('.fd-shell__logo').offsetWidth + this.querySelector('.fd-shell__leading').offsetWidth + this.querySelector('.fd-shell__cart').scrollWidth + 24;
      const compact = matchMedia('(min-width: 75em)').matches && required > available;
      this.toggleAttribute('data-compact', compact);
      nav.inert = compact;
      const height = `${Math.ceil(bar.getBoundingClientRect().height)}px`;
      if (document.body.style.getPropertyValue('--fd-shell-height') !== height) document.body.style.setProperty('--fd-shell-height', height);
    });
  }

  disconnectedCallback() {
    ++this.sequence;
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    for (const record of this.records.values()) {
      record.restore?.();
      if (record.panel.open) record.close();
      unlockScroll(record.panel);
    }
    document.body.style.removeProperty('--fd-shell-height');
  }
}
if (!customElements.get('fd-header')) customElements.define('fd-header', FDHeader);

// Theme-level guard only. Server-enforced checkout validation requires a Shopify Function.
document.addEventListener('submit', event => {
 if (event.target.id !== 'cart-form') return;
 const blocked = document.querySelector('[data-fd-cart-blocked]');
 if (blocked) { event.preventDefault(); event.stopImmediatePropagation(); blocked.focus(); }
}, true);
